import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Actions } from '@components/submenu-with-icon/src/submenu-icon';
import { Tab } from '@components/tabs/src/tabs.type';
import { EVENT_LIST, EVENT_REPORT_ROUTE } from '@constants/routes';
import { MD_VIEWPORT } from '@constants/viewport';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { EventContext } from '@contexts/event/event-context';
import { FloorplanContext } from '@contexts/floorplan/floorplan-context';
import { EventNavigationContext } from '@contexts/navigation/event-navigation-context';
import { NavigationContext } from '@contexts/navigation/navigation-context';
import { EMPTY_FLOORPLAN } from '@domain/floorplan';
import { useDeleteEvent } from '@hooks/events/use-delete-event';
import { useToast } from '@hooks/use-toast/use-toast';
import { useWindowSize } from '@hooks/use-window-size';

import { EventHeader } from '../components/event-header';
import { EventHeaderSkeleton } from '../components/event-header-skeleton';

import { EventManagerNavigationProps, EventManagerNavigationStep } from './event-manager-navigation.type';

export const EventManagerNavigation = ({ children, navigateTo }: EventManagerNavigationProps) => {
  const { clubState, companyState } = useContext(CompanyClubContext);
  const { setFloorplanState } = useContext(FloorplanContext);
  const { eventState, currentStep, setCurrentStep, setEventState } = useContext(EventContext);
  const toast = useToast();
  const { eventNavigationItems } = useContext(EventNavigationContext);
  const { preventRedirect, setIsItemClicked, setRedirectUrl } = useContext(NavigationContext);
  const isLessThanTablet = useWindowSize(MD_VIEWPORT);
  const { mutate: deleteEvent, isSuccess: isDeleteEventSuccess } = useDeleteEvent({ eventId: '' });
  const { t } = useTranslation();

  const EVENT_LIST_PATH = EVENT_LIST.replace(':companyId', companyState.legacyId)
    .replace(':clubSlug', clubState.slug)
    .replace(':view', 'upcoming');

  const NEW_EVENT_REPORT = EVENT_REPORT_ROUTE.replace(':companyId', companyState.legacyId)
    .replace(':clubSlug', clubState.slug)
    .replace(':eventId', eventState?.id as string);

  const tabs: Tab[] = eventNavigationItems.map(({ name, href }) => ({ name: t(`menu:${name}`), id: name, href }));
  const steps: EventManagerNavigationStep[] = eventNavigationItems.map(
    ({ name, hasCheck, isDisabled, href }, index) => ({
      id: index + 1,
      slug: name,
      label: t(`menu:${name}`),
      active: currentStep === name,
      completed: hasCheck ?? false,
      disabled: isDisabled ?? false,
      onClick: () => {
        if (preventRedirect) {
          setRedirectUrl(href);
          setIsItemClicked(true);
          return;
        }
        navigateTo(href);
      },
    })
  );

  const activeStep = eventNavigationItems.reduce((acc, val) => {
    if (val.current) {
      return val.name;
    }
    return acc;
  }, '');

  useEffect(() => {
    setCurrentStep(activeStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, currentStep]);

  const handleDeleteEvent = useCallback(
    (id: string) => {
      deleteEvent({ eventId: id });
    },
    [deleteEvent]
  );
  const handleChangeTab = (value: string) => {
    const clickedTab = tabs.find((tab) => tab.id === value);
    if (preventRedirect) {
      setRedirectUrl(clickedTab?.href as string);
      setIsItemClicked(true);
      return;
    }
    if (clickedTab?.href) navigateTo(clickedTab.href);
  };

  const openPreview = useCallback(() => {
    if (eventState.path) window.open(eventState.path as string, '_blank');
    else
      toast.push({
        title: t('eventList:defaultChannelRequired'),
        text: t('eventList:defaultChannelRequiredDescription'),
        type: 'error',
        disableTimeOut: true,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventState.path, t]);

  const publishedActions: Actions[] = useMemo(() => {
    const baseActions = [
      {
        name: t(eventState.isLiveEvent ? 'eventList:submenuActionPerformance' : 'eventList:submenuActionResults'),
        action: () => navigateTo(NEW_EVENT_REPORT),
      },
      { name: t('eventList:submenuActionDuplicate'), link: eventState.duplicatePath },
    ];

    if (isLessThanTablet)
      return [...baseActions, { name: t('eventDetails:buttonViewText'), action: () => openPreview() }];
    if (eventState.isLiveEvent) return baseActions;
    return [{ name: t('eventDetails:buttonViewText'), action: () => openPreview() }, ...baseActions];
  }, [
    eventState.duplicatePath,
    eventState.isLiveEvent,
    openPreview,
    t,
    isLessThanTablet,
    NEW_EVENT_REPORT,
    navigateTo,
  ]);

  const unpublishedActions: Actions[] = useMemo(() => {
    if (isLessThanTablet)
      return [
        { name: t('eventDetails:buttonPreviewText'), action: () => openPreview() },
        { name: t('eventList:submenuActionDelete'), action: () => handleDeleteEvent(eventState.id as string) },
      ];
    return [{ name: t('eventList:submenuActionDelete'), action: () => handleDeleteEvent(eventState.id as string) }];
  }, [eventState.id, handleDeleteEvent, openPreview, t, isLessThanTablet]);

  useEffect(() => {
    if (isDeleteEventSuccess) {
      navigateTo(EVENT_LIST_PATH);
      setEventState({});
      setFloorplanState(EMPTY_FLOORPLAN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteEventSuccess]);

  return (
    <div className='flex flex-col w-full min-h-screen min-h-[calc(100dvh)] bg-secondary overflow-auto scrollbar-hide'>
      {eventState.name ? (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <EventHeader
          backRoute={EVENT_LIST_PATH}
          isLessThanTablet={isLessThanTablet}
          tabs={tabs}
          currentStep={currentStep}
          handleChangeTab={handleChangeTab}
          steps={steps}
          publishedActions={publishedActions}
          unpublishedActions={unpublishedActions}
          openPreview={openPreview}
          reportPath={NEW_EVENT_REPORT}
          buttonPreviewText={t('eventDetails:buttonPreviewText')}
          buttonViewText={t('eventDetails:buttonViewText')}
          buttonReportText={t(
            eventState.isLiveEvent ? 'eventList:submenuActionPerformance' : 'eventList:submenuActionResults'
          )}
        />
      ) : (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <EventHeaderSkeleton />
      )}
      <main className='flex flex-col w-full max-w-7xl mx-auto mt-8 rounded-2xl pb-28 grow'>{children}</main>
      <footer className='flex flex-end fixed bottom-0 bg-primary shadow-custom w-full shrink-0'>
        <div id='event-navigation-footer' className='w-full '></div>
      </footer>
    </div>
  );
};
